import { FC } from "react";
import { Controller, Control } from "react-hook-form";
import MediaFormats from "types/MediaFormats";
// i18next
import { useTranslation } from "react-i18next";
// MUI
import { ButtonGroup } from "@material-ui/core";
// Icons
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Components
import ImageUpload from "components/ImageUpload";
import StyledMuiButton from "components/StyledMuiButton";
import PromoVideoForm from "pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/PromoVideo/PromoVideoForm";

type Props = {
  control: Control<any>;
  logoFormatWatcher: MediaFormats;
  sponsorImageFormatWatcher: MediaFormats;
  disabled: boolean;
  onBack: () => void;
  onSubmit: () => void;
}

const QuizBrandingStep:FC<Props> = ({ control, logoFormatWatcher, sponsorImageFormatWatcher, disabled, onBack, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-400">{t('dialogs.quizAgent.brandingOptionalHint')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <Controller
            control={control} name="logoFormat"
            render={({ field: { onChange, value } }) => (
              <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                <StyledMuiButton
                  variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.Logo)}
                >{t('labels.squareLogo')}</StyledMuiButton>
                <StyledMuiButton
                  variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.RectLogo)}
                >{t('labels.rectangularLogo')}</StyledMuiButton>
              </ButtonGroup>
            )}
          />
          <Controller
            control={control} name="logoImage"
            render={({ field }) => (
              <ImageUpload
                {...field}
                key={logoFormatWatcher}
                className="w-1/2"
                format={logoFormatWatcher}
                label="labels.logoImage"
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control} name="sponsorImageFormat"
            render={({ field: { onChange, value } }) => (
              <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                <StyledMuiButton
                  variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.Logo)}
                >{t('labels.squareLogo')}</StyledMuiButton>
                <StyledMuiButton
                  variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.RectLogo)}
                >{t('labels.rectangularLogo')}</StyledMuiButton>
              </ButtonGroup>
            )}
          />
          <Controller
            control={control} name="sponsorImage"
            render={({ field }) => (
              <ImageUpload
                {...field}
                key={sponsorImageFormatWatcher}
                className="w-1/2"
                format={sponsorImageFormatWatcher}
                label="pages.quizForm.sponsorImageLabel"
              />
            )}
          />
        </div>
        {/* Logo/sponsor columns have a format-toggle row above their label;
            video has none, so push it down to line up with the upload
            boxes instead of the toggle buttons. */}
        <div className="mt-12">
          <PromoVideoForm label="pages.quizForm.promoVideoLabel" className="w-full" />
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-8 py-3 rounded-xl font-medium text-gray-200 border border-gray-700 hover:bg-[#303035] transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('buttons.back')}
        </button>
        <button
          onClick={onSubmit}
          disabled={disabled}
          className={`
            flex items-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all
            ${disabled
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-500 hover:shadow-lg hover:-translate-y-0.5'
            }
          `}
        >
          <span className="flex items-center gap-2">
            ✨ {t('buttons.proceed')}
            <ChevronRight className="w-5 h-5" />
          </span>
        </button>
      </div>
    </div>
  )
};

export default QuizBrandingStep;
